import React from 'react';
import { Button } from './ui/button';
import { MapPin, Clock, Phone, ExternalLink } from 'lucide-react';

export const Cabinet = () => {
  // Google Maps URL from the requirements
  const googleMapsUrl = "https://www.google.com/maps/place/Cabinet+de+Psychomotricit%C3%A9.Hind+Lirmaqui+Psychomotricienne+Marrakech+.Psychomotricien+%D9%86%D9%81%D8%B3%D8%A7%D9%86%D9%8A+%D8%AD%D8%B1%D9%83%D9%8A%E2%80%AD/@31.632717,-8.0153963,17z/data=!3m1!4b1!4m14!1m7!3m6!1s0xdafefb53d77cde7:0x98645051dcc72d26!2zQ2FiaW5ldCBkZSBQc3ljaG9tb3RyaWNpdMOpLkhpbmQgTGlybWFxdWkgUHN5Y2hvbW90cmljaWVubmUgTWFycmFrZWNoIC5Qc3ljaG9tb3RyaWNpZW4g2YbZgdiz2KfZhtmKINit2LHZg9mK!8m2!3d31.6327125!4d-8.0128214!16s%2Fg%2F11ghffbx71!3m5!1s0xdafefb53d77cde7:0x98645051dcc72d26!8m2!3d31.6327125!4d-8.0128214!16s%2Fg%2F11ghffbx71?entry=ttu&g_ep=EgoyMDI2MDEwNy4wIKXMDSoKLDEwMDc5MjA2OUgBUAM%3D";

  // Embed URL for iframe (using coordinates from the URL)
  const embedUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3397.123!2d-8.0153963!3d31.632717!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xdafefb53d77cde7%3A0x98645051dcc72d26!2sCabinet%20de%20Psychomotricit%C3%A9.Hind%20Lirmaqui%20Psychomotricienne%20Marrakech!5e0!3m2!1sfr!2sma!4v1704672000000!5m2!1sfr!2sma";

  return (
    <section id="cabinet" className="section-padding bg-background relative">
      <div className="max-w-7xl mx-auto container-padding">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <MapPin className="w-4 h-4" />
            <span>Le Cabinet</span>
          </div>

          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground mb-6">
            Un espace pensé pour{' '}
            <span className="text-gradient">le bien-être</span>
          </h2>

          <p className="text-lg text-muted-foreground">
            Un environnement chaleureux et adapté, conçu pour que votre enfant se sente en confiance dès son arrivée.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Map */}
          <div className="order-2 lg:order-1">
            <div className="rounded-2xl overflow-hidden shadow-card bg-card h-[400px] lg:h-full min-h-[400px]">
              <iframe
                src={embedUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Localisation du cabinet de psychomotricité"
              />
            </div>
          </div>

          {/* Info Cards */}
          <div className="order-1 lg:order-2 space-y-6">
            {/* Address Card */}
            <div className="bg-card rounded-2xl p-6 shadow-soft hover:shadow-card transition-shadow duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                    Adresse
                  </h3>
                  <p className="text-muted-foreground mb-1">
                    Cabinet de Psychomotricité
                  </p>
                  <p className="text-muted-foreground mb-1">
                    {/* PLACEHOLDER: Replace with actual address */}
                    52 boulevard moulay Rachid app A4 gueliz
                  </p>
                  <p className="text-muted-foreground">
                    Marrakech, Maroc
                  </p>
                </div>
              </div>
            </div>

            {/* Hours Card */}
            <div className="bg-card rounded-2xl p-6 shadow-soft hover:shadow-card transition-shadow duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-accent-foreground" />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-lg font-semibold text-foreground mb-3">
                    Horaires d'ouverture
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Lundi - Vendredi</span>
                      {/* PLACEHOLDER: Replace with actual hours */}
                      <span className="text-foreground font-medium">9h30 - 19h00</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Samedi</span>
                      {/* PLACEHOLDER: Replace with actual hours */}
                      <span className="text-foreground font-medium">9h30 - 13h00</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Dimanche</span>
                      <span className="text-foreground font-medium">Fermé</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Card */}
            <div className="bg-card rounded-2xl p-6 shadow-soft hover:shadow-card transition-shadow duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-secondary-foreground" />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                    Rendez-vous
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Sur rendez-vous uniquement. Contactez-moi pour planifier une consultation.
                  </p>
                  <a
                    href="#contact"
                    className="text-primary font-medium text-sm hover:underline underline-offset-4 inline-flex items-center gap-1"
                  >
                    Prendre rendez-vous
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Google Maps Button */}
            <Button
              variant="outline"
              size="lg"
              className="w-full"
              asChild
            >
              <a
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                Ouvrir dans Google Maps
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Cabinet;